import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";
import { toast } from "react-toastify";
import {
  FaEye,
  FaEyeSlash,
  FaRobot,
  FaKey,
  FaSync,
  FaTrash,
  FaPaperPlane,
  FaCopy,
  FaPlus,
  FaTimes,
  FaHistory,
  FaChevronRight,
  FaChevronLeft,
  FaCode,
  FaFileAlt,
  FaFileUpload,
  FaFilePdf,
  FaFileWord,
  FaFileCsv,
  FaAlignLeft,
  FaDatabase,
  FaSlidersH,
  FaRocket,
  FaCommentDots,
  FaQuestionCircle,
  FaEdit,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";

const Transcriptionconfig = () => {
  const { user } = useContext(AuthContext);

  /** --------------------------- shared utils --------------------------- */
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  /** --------------------------- section switcher (With Session Storage) --------------------------- */
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('transcriptionActiveSection') || "transcription";
    }
    return "transcription";
  }); // "transcription" | "chatbot"

  useEffect(() => {
    sessionStorage.setItem('transcriptionActiveSection', activeSection);
  }, [activeSection]);

  /** =========================================================================
   *  SECTION 1: TRANSCRIPTION CONFIGURATION (unchanged, same API as before)
   *  ========================================================================= */
  const [transcriptionKey, setTranscriptionKey] = useState("");
  const [isSavingTranscription, setIsSavingTranscription] = useState(false);
  const [showTranscriptionKey, setShowTranscriptionKey] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchTranscriptionKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchTranscriptionKey = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/gemini-api-key/${companyId}/company`,
        { headers: authHeader }
      );
      if (response.data && response.data.maskedApiKey) {
        setTranscriptionKey(response.data.maskedApiKey);
      }
    } catch (error) {
      console.error("Failed to fetch transcription API key:", error);
    }
  };

  const saveTranscriptionKey = async () => {
    if (!transcriptionKey.trim()) {
      toast.error("Please enter a valid API key");
      return;
    }

    if (transcriptionKey.includes("••••")) {
      toast.error("Please enter a new API key before saving.");
      return;
    }

    try {
      setIsSavingTranscription(true);
      await axios.post(
        `${API_URL}/api/gemini-api-key/${companyId}`,
        { apiKey: transcriptionKey },
        {
          headers: {
            ...authHeader,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Transcription API Key saved successfully");
    } catch (error) {
      console.error("Failed to save transcription API key:", error);
      toast.error("Failed to save Transcription API Key");
    } finally {
      setIsSavingTranscription(false);
    }
  };

  /** =========================================================================
   *  SECTION 2: CHATBOT CONFIGURATION (new)
   *  ========================================================================= */

  const defaultChatbotForm = {
    name: "",
    systemPrompt: "",
    welcomeMessage: "",
    allowedTopics: [],
    restrictToAllowedTopics: false,
    offTopicResponse: "",
    allowedOrigins: [],
    model: "gemini-3.6-flash",
    temperature: 0.4,
    maxHistoryTurns: 12,
    rateLimitPerMinute: 20,
    active: true,
  };

  const [chatbotConfig, setChatbotConfig] = useState(null);
  const [chatbotExists, setChatbotExists] = useState(false);
  const [chatbotForm, setChatbotForm] = useState(defaultChatbotForm);
  const [isLoadingChatbot, setIsLoadingChatbot] = useState(false);
  const [isSavingChatbot, setIsSavingChatbot] = useState(false);
  const [isRegeneratingKey, setIsRegeneratingKey] = useState(false);
  const [isDeletingChatbot, setIsDeletingChatbot] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showWidgetKey, setShowWidgetKey] = useState(false);
  const [hasFetchedChatbot, setHasFetchedChatbot] = useState(false);

  const [topicInput, setTopicInput] = useState("");
  const [originInput, setOriginInput] = useState("");

  // Test message panel
  const [testSessionId, setTestSessionId] = useState(null);
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Conversations
  const [conversations, setConversations] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [hasFetchedConversations, setHasFetchedConversations] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isLoadingConversationDetail, setIsLoadingConversationDetail] = useState(false);

  /** --------------------------- chatbot: sub-navigation (With Session Storage) --------------------------- */
  const [chatbotSubTab, setChatbotSubTab] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('transcriptionChatbotSubTab') || "setup";
    }
    return "setup";
  });

  useEffect(() => {
    sessionStorage.setItem('transcriptionChatbotSubTab', chatbotSubTab);
  }, [chatbotSubTab]);

  const baseChatbotTabs = [
    { id: "setup", label: "Setup", icon: <FaRobot size={11} /> },
    { id: "behavior", label: "Behavior & Limits", icon: <FaSlidersH size={11} /> },
  ];
  const advancedChatbotTabs = [
    { id: "knowledge", label: "Knowledge Base", icon: <FaDatabase size={11} /> },
    { id: "deploy", label: "Widget & Embed", icon: <FaRocket size={11} /> },
    { id: "test", label: "Test Bot", icon: <FaCommentDots size={11} /> },
    { id: "faqs", label: "FAQs", icon: <FaQuestionCircle size={11} /> },
    { id: "conversations", label: "Conversations", icon: <FaHistory size={11} /> },
  ];
  const visibleChatbotTabs = chatbotExists
    ? [...baseChatbotTabs, ...advancedChatbotTabs]
    : baseChatbotTabs;

  // FIXED: Added hasFetchedChatbot to ensure it only resets after checking the API
  useEffect(() => {
    const advancedIds = advancedChatbotTabs.map((t) => t.id);
    if (hasFetchedChatbot && !chatbotExists && advancedIds.includes(chatbotSubTab)) {
      setChatbotSubTab("setup");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotExists, hasFetchedChatbot]);

  useEffect(() => {
    if (activeSection === "chatbot" && companyId && !hasFetchedChatbot) {
      fetchChatbotConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, companyId]);

  useEffect(() => {
    if (
      chatbotSubTab === "conversations" &&
      chatbotExists &&
      !hasFetchedConversations
    ) {
      fetchConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotSubTab, chatbotExists]);

  useEffect(() => {
    if (chatbotSubTab === "faqs" && chatbotExists && !hasFetchedFaqs) {
      fetchFaqs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotSubTab, chatbotExists]);

  /** --------------------------- chatbot: config CRUD --------------------------- */

  const fetchChatbotConfig = async () => {
    try {
      setIsLoadingChatbot(true);
      const response = await axios.get(
        `${API_URL}/api/chatbot/config/${companyId}`,
        { headers: authHeader }
      );
      if (response.data) {
        setChatbotConfig(response.data);
        setChatbotExists(true);
        setChatbotForm({
          name: response.data.name || "",
          systemPrompt: response.data.systemPrompt || "",
          welcomeMessage: response.data.welcomeMessage || "",
          allowedTopics: response.data.allowedTopics || [],
          restrictToAllowedTopics: !!response.data.restrictToAllowedTopics,
          offTopicResponse: response.data.offTopicResponse || "",
          allowedOrigins: response.data.allowedOrigins || [],
          model: response.data.model || "gemini-3.6-flash",
          temperature:
            response.data.temperature !== undefined ? response.data.temperature : 0.4,
          maxHistoryTurns: response.data.maxHistoryTurns || 12,
          rateLimitPerMinute: response.data.rateLimitPerMinute || 20,
          active: response.data.active !== undefined ? response.data.active : true,
        });
        if (Array.isArray(response.data.knowledgeSources)) {
          setKnowledgeSources(response.data.knowledgeSources);
        } else {
          fetchKnowledgeSources();
        }
        if (Array.isArray(response.data.faqs)) {
          setFaqs(response.data.faqs);
          setHasFetchedFaqs(true);
        }
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        setChatbotExists(false);
        setChatbotConfig(null);
        setChatbotForm(defaultChatbotForm);
      } else {
        console.error("Failed to fetch chatbot configuration:", error);
        toast.error("Failed to load chatbot configuration");
      }
    } finally {
      setIsLoadingChatbot(false);
      setHasFetchedChatbot(true);
    }
  };

  const saveChatbotConfig = async () => {
    if (!chatbotForm.name.trim()) {
      toast.error("Please enter a bot name");
      setChatbotSubTab("setup");
      return;
    }
    if (!chatbotForm.systemPrompt.trim()) {
      toast.error("Please enter a system prompt");
      setChatbotSubTab("setup");
      return;
    }

    try {
      setIsSavingChatbot(true);
      const payload = {
        name: chatbotForm.name.trim(),
        systemPrompt: chatbotForm.systemPrompt.trim(),
        welcomeMessage: chatbotForm.welcomeMessage.trim(),
        allowedTopics: chatbotForm.allowedTopics,
        restrictToAllowedTopics: chatbotForm.restrictToAllowedTopics,
        offTopicResponse: chatbotForm.offTopicResponse.trim(),
        allowedOrigins: chatbotForm.allowedOrigins,
        model: chatbotForm.model,
        temperature: Number(chatbotForm.temperature),
        maxHistoryTurns: Number(chatbotForm.maxHistoryTurns),
        rateLimitPerMinute: Number(chatbotForm.rateLimitPerMinute),
        active: chatbotForm.active,
      };

      const response = await axios.put(
        `${API_URL}/api/chatbot/config/${companyId}`,
        payload,
        {
          headers: {
            ...authHeader,
            "Content-Type": "application/json",
          },
        }
      );

      const wasNew = !chatbotExists;
      setChatbotConfig(response.data);
      setChatbotExists(true);
      if (Array.isArray(response.data.knowledgeSources)) {
        setKnowledgeSources(response.data.knowledgeSources);
      }
      if (Array.isArray(response.data.faqs)) {
        setFaqs(response.data.faqs);
      }
      toast.success("Chatbot configuration saved successfully");
      if (wasNew) {
        setChatbotSubTab("knowledge");
      }
    } catch (error) {
      console.error("Failed to save chatbot configuration:", error);
      toast.error("Failed to save chatbot configuration");
    } finally {
      setIsSavingChatbot(false);
    }
  };

  const regenerateWidgetKey = async () => {
    try {
      setIsRegeneratingKey(true);
      const response = await axios.post(
        `${API_URL}/api/chatbot/config/${companyId}/regenerate-widget-key`,
        {},
        { headers: authHeader }
      );
      setChatbotConfig(response.data);
      toast.success("Widget key regenerated successfully");
    } catch (error) {
      console.error("Failed to regenerate widget key:", error);
      toast.error("Failed to regenerate widget key");
    } finally {
      setIsRegeneratingKey(false);
    }
  };

  const deleteChatbotConfig = async () => {
    try {
      setIsDeletingChatbot(true);
      await axios.delete(`${API_URL}/api/chatbot/config/${companyId}`, {
        headers: authHeader,
      });
      toast.success("Chatbot configuration deleted");
      setChatbotConfig(null);
      setChatbotExists(false);
      setChatbotForm(defaultChatbotForm);
      setTestMessages([]);
      setTestSessionId(null);
      setConversations([]);
      setHasFetchedConversations(false);
      setSelectedConversation(null);
      setShowDeleteConfirm(false);
      setKnowledgeSources([]);
      setFaqs([]);
      setHasFetchedFaqs(false);
      resetFaqForm();
      setFaqToDelete(null);
      setChatbotSubTab("setup");
    } catch (error) {
      console.error("Failed to delete chatbot configuration:", error);
      toast.error("Failed to delete chatbot configuration");
    } finally {
      setIsDeletingChatbot(false);
    }
  };

  /** --------------------------- chatbot: tag list helpers --------------------------- */

  const addTopic = () => {
    const value = topicInput.trim();
    if (!value) return;
    if (chatbotForm.allowedTopics.includes(value)) {
      setTopicInput("");
      return;
    }
    setChatbotForm((prev) => ({
      ...prev,
      allowedTopics: [...prev.allowedTopics, value],
    }));
    setTopicInput("");
  };

  const removeTopic = (topic) => {
    setChatbotForm((prev) => ({
      ...prev,
      allowedTopics: prev.allowedTopics.filter((t) => t !== topic),
    }));
  };

  const addOrigin = () => {
    const value = originInput.trim().replace(/\/$/, "");
    if (!value) return;
    if (chatbotForm.allowedOrigins.includes(value)) {
      setOriginInput("");
      return;
    }
    setChatbotForm((prev) => ({
      ...prev,
      allowedOrigins: [...prev.allowedOrigins, value],
    }));
    setOriginInput("");
  };

  const removeOrigin = (origin) => {
    setChatbotForm((prev) => ({
      ...prev,
      allowedOrigins: prev.allowedOrigins.filter((o) => o !== origin),
    }));
  };

  /** --------------------------- chatbot: test message --------------------------- */

  const sendTestMessage = async () => {
    if (!testInput.trim()) return;
    const outgoing = testInput.trim();
    setTestMessages((prev) => [...prev, { role: "user", text: outgoing }]);
    setTestInput("");

    try {
      setIsSendingTest(true);
      const response = await axios.post(
        `${API_URL}/api/chatbot/config/${companyId}/test-message`,
        { sessionId: testSessionId, message: outgoing },
        {
          headers: {
            ...authHeader,
            "Content-Type": "application/json",
          },
        }
      );
      setTestSessionId(response.data.sessionId);
      setTestMessages((prev) => [
        ...prev,
        { role: "model", text: response.data.reply },
      ]);
    } catch (error) {
      console.error("Failed to send test message:", error);
      toast.error("Failed to send test message");
      setTestMessages((prev) => [
        ...prev,
        { role: "model", text: "⚠️ Something went wrong. Please try again." },
      ]);
    } finally {
      setIsSendingTest(false);
    }
  };

  const resetTestConversation = () => {
    setTestSessionId(null);
    setTestMessages([]);
  };

  /** --------------------------- chatbot: conversations --------------------------- */

  const fetchConversations = async () => {
    try {
      setIsLoadingConversations(true);
      const response = await axios.get(
        `${API_URL}/api/chatbot/config/${companyId}/conversations`,
        { headers: authHeader }
      );
      setConversations(response.data || []);
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
      toast.error("Failed to load conversations");
    } finally {
      setIsLoadingConversations(false);
      setHasFetchedConversations(true);
    }
  };

  const fetchConversationDetail = async (sessionId) => {
    try {
      setIsLoadingConversationDetail(true);
      const response = await axios.get(
        `${API_URL}/api/chatbot/config/${companyId}/conversations/${sessionId}`,
        { headers: authHeader }
      );
      setSelectedConversation(response.data);
    } catch (error) {
      console.error("Failed to fetch conversation:", error);
      toast.error("Failed to load conversation");
    } finally {
      setIsLoadingConversationDetail(false);
    }
  };

  /** =========================================================================
   *  SECTION 2b: CHATBOT KNOWLEDGE SOURCES (new)
   *  ========================================================================= */

  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [isLoadingKnowledge, setIsLoadingKnowledge] = useState(false);

  // Add pasted text
  const [knowledgeTitle, setKnowledgeTitle] = useState("");
  const [knowledgeContent, setKnowledgeContent] = useState("");
  const [isAddingKnowledgeText, setIsAddingKnowledgeText] = useState(false);

  // Upload file
  const [knowledgeFile, setKnowledgeFile] = useState(null);
  const [knowledgeFileTitle, setKnowledgeFileTitle] = useState("");
  const [isUploadingKnowledgeFile, setIsUploadingKnowledgeFile] = useState(false);

  // Delete
  const [deletingSourceId, setDeletingSourceId] = useState(null);

  const fetchKnowledgeSources = async () => {
    if (!companyId) return;
    try {
      setIsLoadingKnowledge(true);
      const response = await axios.get(
        `${API_URL}/api/chatbot/config/${companyId}/knowledge`,
        { headers: authHeader }
      );
      setKnowledgeSources(response.data || []);
    } catch (error) {
      if (error?.response?.status === 404) {
        setKnowledgeSources([]);
      } else {
        console.error("Failed to fetch knowledge sources:", error);
        toast.error("Failed to load knowledge sources");
      }
    } finally {
      setIsLoadingKnowledge(false);
    }
  };

  const addTextKnowledge = async () => {
    if (!knowledgeContent.trim()) {
      toast.error("Please enter some content to add");
      return;
    }

    try {
      setIsAddingKnowledgeText(true);
      const response = await axios.post(
        `${API_URL}/api/chatbot/config/${companyId}/knowledge/text`,
        {
          title: knowledgeTitle.trim(),
          content: knowledgeContent.trim(),
        },
        {
          headers: {
            ...authHeader,
            "Content-Type": "application/json",
          },
        }
      );
      setChatbotConfig(response.data);
      if (Array.isArray(response.data.knowledgeSources)) {
        setKnowledgeSources(response.data.knowledgeSources);
      } else {
        fetchKnowledgeSources();
      }
      setKnowledgeTitle("");
      setKnowledgeContent("");
      toast.success("Text added to knowledge base");
    } catch (error) {
      console.error("Failed to add text knowledge source:", error);
      toast.error(
        error?.response?.data?.message || "Failed to add text to knowledge base"
      );
    } finally {
      setIsAddingKnowledgeText(false);
    }
  };

  const uploadKnowledgeFile = async () => {
    if (!knowledgeFile) {
      toast.error("Please choose a file to upload");
      return;
    }

    try {
      setIsUploadingKnowledgeFile(true);
      const formData = new FormData();
      formData.append("file", knowledgeFile);
      if (knowledgeFileTitle.trim()) {
        formData.append("title", knowledgeFileTitle.trim());
      }

      const response = await axios.post(
        `${API_URL}/api/chatbot/config/${companyId}/knowledge/upload`,
        formData,
        {
          headers: {
            ...authHeader,
          },
        }
      );
      setChatbotConfig(response.data);
      if (Array.isArray(response.data.knowledgeSources)) {
        setKnowledgeSources(response.data.knowledgeSources);
      } else {
        fetchKnowledgeSources();
      }
      setKnowledgeFile(null);
      setKnowledgeFileTitle("");
      toast.success("File uploaded to knowledge base");
    } catch (error) {
      console.error("Failed to upload knowledge file:", error);
      toast.error(
        error?.response?.data?.message || "Failed to upload file to knowledge base"
      );
    } finally {
      setIsUploadingKnowledgeFile(false);
    }
  };

  const deleteKnowledgeSource = async (sourceId) => {
    try {
      setDeletingSourceId(sourceId);
      const response = await axios.delete(
        `${API_URL}/api/chatbot/config/${companyId}/knowledge/${sourceId}`,
        { headers: authHeader }
      );
      setChatbotConfig(response.data);
      if (Array.isArray(response.data.knowledgeSources)) {
        setKnowledgeSources(response.data.knowledgeSources);
      } else {
        setKnowledgeSources((prev) => prev.filter((s) => s.id !== sourceId));
      }
      toast.success("Knowledge source deleted");
    } catch (error) {
      console.error("Failed to delete knowledge source:", error);
      toast.error(
        error?.response?.data?.message || "Failed to delete knowledge source"
      );
    } finally {
      setDeletingSourceId(null);
    }
  };

  const knowledgeSourceIcon = (sourceType) => {
    switch (sourceType) {
      case "PDF":
        return <FaFilePdf size={12} className="text-red-500" />;
      case "DOCX":
        return <FaFileWord size={12} className="text-blue-500" />;
      case "CSV":
        return <FaFileCsv size={12} className="text-green-600" />;
      case "TEXT":
        return <FaAlignLeft size={12} className="text-[#8B5CF6]" />;
      default:
        return <FaFileAlt size={12} className="text-gray-400" />;
    }
  };

  /** =========================================================================
   *  SECTION 2c: CHATBOT FAQs
   *  ========================================================================= */

  const defaultFaqForm = {
    question: "",
    answer: "",
    parentId: null,
    active: true,
  };

  const [faqs, setFaqs] = useState([]);
  const [isLoadingFaqs, setIsLoadingFaqs] = useState(false);
  const [hasFetchedFaqs, setHasFetchedFaqs] = useState(false);

  const [faqForm, setFaqForm] = useState(defaultFaqForm);
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [isSavingFaq, setIsSavingFaq] = useState(false);
  const [togglingFaqId, setTogglingFaqId] = useState(null);
  const [deletingFaqId, setDeletingFaqId] = useState(null);
  const [faqToDelete, setFaqToDelete] = useState(null);

  const faqErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data?.message) return data.message;
    return fallback;
  };

  const fetchFaqs = async () => {
    if (!companyId) return;
    try {
      setIsLoadingFaqs(true);
      const response = await axios.get(
        `${API_URL}/api/chatbot/config/${companyId}/faqs`,
        { headers: authHeader }
      );
      setFaqs(response.data || []);
    } catch (error) {
      if (error?.response?.status === 404) {
        setFaqs([]);
      } else {
        console.error("Failed to fetch FAQs:", error);
        toast.error("Failed to load FAQs");
      }
    } finally {
      setIsLoadingFaqs(false);
      setHasFetchedFaqs(true);
    }
  };

  const resetFaqForm = () => {
    setFaqForm(defaultFaqForm);
    setEditingFaqId(null);
  };

  const startEditFaq = (faq) => {
    setEditingFaqId(faq.id);
    setFaqForm({
      question: faq.question || "",
      answer: faq.answer || "",
      parentId: faq.parentId || null,
      active: faq.active !== undefined ? faq.active : true,
    });
  };

  const buildFaqTree = (flatList) => {
    const map = {};
    flatList.forEach((f) => {
      map[f.id] = { ...f, children: [] };
    });
    const roots = [];
    flatList.forEach((f) => {
      if (f.parentId && map[f.parentId]) {
        map[f.parentId].children.push(map[f.id]);
      } else {
        roots.push(map[f.id]);
      }
    });
    return roots;
  };

  const getDescendantIds = (id, list) => {
    const result = [];
    const stack = [id];
    while (stack.length) {
      const current = stack.pop();
      list.forEach((f) => {
        if (f.parentId === current) {
          result.push(f.id);
          stack.push(f.id);
        }
      });
    }
    return result;
  };

  const getFaqDepth = (faqId, list) => {
    let depth = 0;
    let currentId = faqId;
    const guard = new Set();
    while (currentId && !guard.has(currentId)) {
      guard.add(currentId);
      const current = list.find((f) => f.id === currentId);
      if (!current || !current.parentId) break;
      depth += 1;
      currentId = current.parentId;
    }
    return depth;
  };

  const faqTree = buildFaqTree(faqs);

  const excludedParentIds = editingFaqId
    ? [editingFaqId, ...getDescendantIds(editingFaqId, faqs)]
    : [];

  const parentFaqOptions = faqs
    .filter((f) => !excludedParentIds.includes(f.id))
    .slice()
    .sort((a, b) => (a.question || "").localeCompare(b.question || ""));

  const saveFaq = async () => {
    if (!faqForm.question.trim()) {
      toast.error("Please enter a question");
      return;
    }
    if (!faqForm.answer.trim()) {
      toast.error("Please enter an answer");
      return;
    }

    const payload = {
      question: faqForm.question.trim(),
      answer: faqForm.answer.trim(),
      parentId: faqForm.parentId || null,
      active: faqForm.active,
    };

    try {
      setIsSavingFaq(true);
      let response;
      if (editingFaqId) {
        response = await axios.put(
          `${API_URL}/api/chatbot/config/${companyId}/faqs/${editingFaqId}`,
          payload,
          { headers: { ...authHeader, "Content-Type": "application/json" } }
        );
      } else {
        response = await axios.post(
          `${API_URL}/api/chatbot/config/${companyId}/faqs`,
          payload,
          { headers: { ...authHeader, "Content-Type": "application/json" } }
        );
      }
      setChatbotConfig(response.data);
      if (Array.isArray(response.data.faqs)) {
        setFaqs(response.data.faqs);
      } else {
        fetchFaqs();
      }
      toast.success(editingFaqId ? "FAQ updated successfully" : "FAQ added successfully");
      resetFaqForm();
    } catch (error) {
      console.error("Failed to save FAQ:", error);
      toast.error(faqErrorMessage(error, "Failed to save FAQ"));
    } finally {
      setIsSavingFaq(false);
    }
  };

  const toggleFaqActive = async (faq) => {
    try {
      setTogglingFaqId(faq.id);
      const response = await axios.put(
        `${API_URL}/api/chatbot/config/${companyId}/faqs/${faq.id}`,
        { active: !faq.active },
        { headers: { ...authHeader, "Content-Type": "application/json" } }
      );
      setChatbotConfig(response.data);
      if (Array.isArray(response.data.faqs)) {
        setFaqs(response.data.faqs);
      } else {
        setFaqs((prev) =>
          prev.map((f) => (f.id === faq.id ? { ...f, active: !f.active } : f))
        );
      }
      toast.success(`FAQ ${!faq.active ? "activated" : "deactivated"}`);
    } catch (error) {
      console.error("Failed to update FAQ:", error);
      toast.error(faqErrorMessage(error, "Failed to update FAQ"));
    } finally {
      setTogglingFaqId(null);
    }
  };

  const askDeleteFaq = (faq) => {
    setFaqToDelete(faq);
  };

  const cancelDeleteFaq = () => {
    setFaqToDelete(null);
  };

  const deleteFaq = async (faqId) => {
    try {
      setDeletingFaqId(faqId);
      const response = await axios.delete(
        `${API_URL}/api/chatbot/config/${companyId}/faqs/${faqId}`,
        { headers: authHeader }
      );
      setChatbotConfig(response.data);
      if (Array.isArray(response.data.faqs)) {
        setFaqs(response.data.faqs);
      } else {
        const removedIds = [faqId, ...getDescendantIds(faqId, faqs)];
        setFaqs((prev) => prev.filter((f) => !removedIds.includes(f.id)));
      }
      if (editingFaqId === faqId) {
        resetFaqForm();
      }
      toast.success("FAQ deleted");
    } catch (error) {
      console.error("Failed to delete FAQ:", error);
      toast.error(faqErrorMessage(error, "Failed to delete FAQ"));
    } finally {
      setDeletingFaqId(null);
      setFaqToDelete(null);
    }
  };

  const renderFaqNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    return (
      <div key={node.id}>
        <div
          style={{ marginLeft: depth * 16 }}
          className={`px-3 py-2.5 rounded border ${
            node.active
              ? "bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-700"
              : "bg-gray-50/50 dark:bg-gray-900/30 border-dashed border-gray-200 dark:border-gray-700 opacity-70"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {hasChildren ? (
                  <FaDatabase size={10} className="text-[#8B5CF6] flex-shrink-0" />
                ) : (
                  <FaQuestionCircle size={10} className="text-gray-400 flex-shrink-0" />
                )}
                <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                  {node.question}
                </p>
                {hasChildren && (
                  <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-[#F3E8FF] dark:bg-purple-900/40 text-[#673AB7] dark:text-[#C4B5FD] rounded">
                    Category · {node.children.length}
                  </span>
                )}
                {!node.active && (
                  <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300 rounded">
                    Hidden
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {node.answer}
              </p>
              <p className="text-[10px] text-gray-400 mt-1.5">
                Added {formatDate(node.addedAt)}
                {node.updatedAt && node.updatedAt !== node.addedAt
                  ? ` · Updated ${formatDate(node.updatedAt)}`
                  : ""}
              </p>
            </div>
            <div className="flex items-center gap-2.5 flex-shrink-0 mt-0.5">
              <button
                type="button"
                onClick={() => toggleFaqActive(node)}
                disabled={togglingFaqId === node.id}
                title={node.active ? "Hide from widget" : "Show in widget"}
                className="text-gray-400 hover:text-[#673AB7] disabled:opacity-50"
              >
                {node.active ? (
                  <FaToggleOn size={16} className="text-[#673AB7]" />
                ) : (
                  <FaToggleOff size={16} />
                )}
              </button>
              <button
                type="button"
                onClick={() => startEditFaq(node)}
                title="Edit"
                className="text-gray-400 hover:text-[#673AB7]"
              >
                <FaEdit size={12} />
              </button>
              <button
                type="button"
                onClick={() => askDeleteFaq(node)}
                disabled={deletingFaqId === node.id}
                title="Delete"
                className="text-gray-400 hover:text-red-500 disabled:opacity-50"
              >
                <FaTrash size={12} />
              </button>
            </div>
          </div>
        </div>
        {hasChildren && (
          <div className="mt-2 space-y-2">
            {node.children.map((child) => renderFaqNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  /** --------------------------- chatbot: misc --------------------------- */

  const copyToClipboard = (value, label = "Copied to clipboard") => {
    if (!value) return;
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success(label))
      .catch(() => toast.error("Failed to copy"));
  };

  const AUTO_RESIZE_MAX_HEIGHT = 160;

  const autoResizeRef = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, AUTO_RESIZE_MAX_HEIGHT)}px`;
  };

  const embedSnippet = chatbotConfig?.widgetKey
    ? `<script
  src="${window.location.origin}/widget/chatbot-widget.js"
  data-widget-key="${chatbotConfig.widgetKey}"
  data-api-base="${window.location.origin}"
  defer>
</script>`
    : "";

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

 /** =========================================================================
   *  RENDER
   *  ========================================================================= */
  return (
    <div className="mx-auto bg-[#FAF8FF] dark:bg-gray-900 p-5 rounded-xl shadow-sm transition-all duration-200 min-h-screen cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-bold bg-gradient-to-r from-[#8B5CF6] to-[#673AB7] bg-clip-text text-transparent" style={{ fontFamily: "'Sora', sans-serif" }}>
          AI Configuration
        </h2>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200 dark:border-gray-700 max-w-3xl">
        <button
          onClick={() => setActiveSection("transcription")}
          className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${
            activeSection === "transcription"
              ? "text-[#673AB7] dark:text-[#8B5CF6] border-b-2 border-[#673AB7] dark:border-[#8B5CF6]"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          Transcription Configuration
        </button>
        <button
          onClick={() => setActiveSection("chatbot")}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${
            activeSection === "chatbot"
              ? "text-[#673AB7] dark:text-[#8B5CF6] border-b-2 border-[#673AB7] dark:border-[#8B5CF6]"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          <FaRobot size={12} />
          Chatbot Configuration
        </button>
      </div>

      {/* ================= TRANSCRIPTION SECTION ================= */}
      {activeSection === "transcription" && (
        <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700 max-w-3xl">
          <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white mb-3">
            Gemini API Configuration
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
            Enter your API key below to enable transcription features for your account.
          </p>

          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1 w-full relative">
              <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                API Key
              </label>
              <input
                type={showTranscriptionKey ? "text" : "password"}
                placeholder="Enter Transcription API Key (AIza...)"
                value={transcriptionKey}
                onChange={(e) => setTranscriptionKey(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
              />
              <button
                type="button"
                className="absolute right-2.5 top-[26px] text-gray-500 dark:text-gray-400 hover:text-gray-700"
                onClick={() => setShowTranscriptionKey(!showTranscriptionKey)}
              >
                {showTranscriptionKey ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
              </button>
            </div>
            <button
              onClick={saveTranscriptionKey}
              disabled={isSavingTranscription || !transcriptionKey.trim()}
              className="px-4 py-1.5 text-xs bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50 transition-colors h-[32px]"
            >
              {isSavingTranscription ? "Saving..." : "Save Key"}
            </button>
          </div>
        </div>
      )}

      {/* ================= CHATBOT SECTION ================= */}
      {activeSection === "chatbot" && (
        <div className="max-w-3xl">
          {isLoadingChatbot ? (
            <div className="p-8 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
              Loading chatbot configuration...
            </div>
          ) : (
            <>
              {/* Status banner */}
              {!chatbotExists && (
                <div className="p-3 bg-[#F3E8FF] dark:bg-blue-900/20 border border-[#E9D5FF] dark:border-blue-800 rounded text-xs text-[#673AB7] dark:text-blue-300 mb-4">
                  No chatbot configured yet. Fill in the Setup tab below and save to
                  create one — Knowledge Base, Widget &amp; Embed, Test Bot, FAQs and
                  Conversations will unlock right after.
                </div>
              )}

              {/* Chatbot sub-navigation */}
              <div className="flex flex-wrap gap-1.5 mb-4 border-b border-gray-200 dark:border-gray-700">
                {visibleChatbotTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setChatbotSubTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${
                      chatbotSubTab === tab.id
                        ? "text-[#673AB7] dark:text-[#8B5CF6] border-b-2 border-[#673AB7] dark:border-[#8B5CF6] bg-white dark:bg-gray-800"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="space-y-5">
                {/* ---------------- TAB: SETUP ---------------- */}
                {chatbotSubTab === "setup" && (
                  <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white">
                          Bot Details
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          The basics — what your bot is called and how it introduces
                          itself.
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none flex-shrink-0">
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {chatbotForm.active ? "Active" : "Inactive"}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setChatbotForm((prev) => ({ ...prev, active: !prev.active }))
                          }
                          className={`relative w-8 h-4 rounded-full transition-colors ${
                            chatbotForm.active
                              ? "bg-[#673AB7]"
                              : "bg-gray-300 dark:bg-gray-600"
                          }`}
                        >
                          <span
                            className={`absolute top-[2px] left-[2px] w-3 h-3 bg-white rounded-full transition-transform ${
                              chatbotForm.active ? "translate-x-4" : ""
                            }`}
                          />
                        </button>
                      </label>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                          Bot Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Acme Support Bot"
                          value={chatbotForm.name}
                          onChange={(e) =>
                            setChatbotForm((prev) => ({ ...prev, name: e.target.value }))
                          }
                          className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                          Welcome Message
                        </label>
                        <textarea
                          ref={autoResizeRef}
                          rows={1}
                          placeholder="Hi! Ask me anything about our product."
                          value={chatbotForm.welcomeMessage}
                          onChange={(e) =>
                            setChatbotForm((prev) => ({
                              ...prev,
                              welcomeMessage: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                            }
                          }}
                          className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] resize-none overflow-y-auto leading-relaxed transition-[height] duration-150 ease-in-out"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                          System Prompt
                        </label>
                        <textarea
                          rows={4}
                          placeholder="You are a helpful assistant for Acme Corp."
                          value={chatbotForm.systemPrompt}
                          onChange={(e) =>
                            setChatbotForm((prev) => ({
                              ...prev,
                              systemPrompt: e.target.value,
                            }))
                          }
                          className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] resize-y"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ---------------- TAB: BEHAVIOR & LIMITS ---------------- */}
                {chatbotSubTab === "behavior" && (
                  <div className="space-y-5">
                    {/* Topic Restrictions */}
                    <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                      <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white mb-3">
                        Topic Restrictions
                      </h3>

                      <div className="mb-3">
                        <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                          Allowed Topics
                        </label>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="text"
                            placeholder="e.g. pricing"
                            value={topicInput}
                            onChange={(e) => setTopicInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addTopic();
                              }
                            }}
                            className="flex-1 px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                          />
                          <button
                            type="button"
                            onClick={addTopic}
                            className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 dark:text-white rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                          >
                            <FaPlus size={10} /> Add
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {chatbotForm.allowedTopics.map((topic) => (
                            <span
                              key={topic}
                              className="flex items-center gap-1 px-2 py-0.5 bg-[#F3E8FF] dark:bg-purple-900/40 text-[#5E35A6] dark:text-[#C4B5FD] rounded-full text-[11px]"
                            >
                              {topic}
                              <button
                                type="button"
                                onClick={() => removeTopic(topic)}
                                className="hover:text-[#4C2D85] dark:hover:text-[#F3E8FF]"
                              >
                                <FaTimes size={9} />
                              </button>
                            </span>
                          ))}
                          {chatbotForm.allowedTopics.length === 0 && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              No topics added yet
                            </span>
                          )}
                        </div>
                      </div>

                      <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={chatbotForm.restrictToAllowedTopics}
                          onChange={(e) =>
                            setChatbotForm((prev) => ({
                              ...prev,
                              restrictToAllowedTopics: e.target.checked,
                            }))
                          }
                          className="w-3.5 h-3.5 accent-purple-600"
                        />
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          Restrict the bot to only answer questions about allowed topics
                        </span>
                      </label>

                      <div>
                        <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                          Off-Topic Response
                        </label>
                        <textarea
                          ref={autoResizeRef}
                          rows={1}
                          placeholder="I can only help with product-related questions."
                          value={chatbotForm.offTopicResponse}
                          onChange={(e) =>
                            setChatbotForm((prev) => ({
                              ...prev,
                              offTopicResponse: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                            }
                          }}
                          className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] resize-none overflow-y-auto leading-relaxed transition-[height] duration-150 ease-in-out"
                        />
                      </div>
                    </div>

                    {/* Model & Limits */}
                    <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                      <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white mb-3">
                        Model &amp; Limits
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                            Model
                          </label>
                          <select
                            value={chatbotForm.model}
                            onChange={(e) =>
                              setChatbotForm((prev) => ({ ...prev, model: e.target.value }))
                            }
                            className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                          >
                            <option value="gemini-3.7-flash">gemini-3.7-flash</option>
                            <option value="gemini-3.6-flash">gemini-3.6-flash</option>
                            <option value="gemini-3.5-flash">gemini-3.5-flash</option>
                            <option value="gemini-3.5-flash-lite">gemini-3.5-flash-lite</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                            Temperature ({Number(chatbotForm.temperature).toFixed(1)})
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={chatbotForm.temperature}
                            onChange={(e) =>
                              setChatbotForm((prev) => ({
                                ...prev,
                                temperature: e.target.value,
                              }))
                            }
                            className="w-full accent-purple-600 mt-2"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                            Max History Turns
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={chatbotForm.maxHistoryTurns}
                            onChange={(e) =>
                              setChatbotForm((prev) => ({
                                ...prev,
                                maxHistoryTurns: e.target.value,
                              }))
                            }
                            className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                            Rate Limit (per minute)
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={chatbotForm.rateLimitPerMinute}
                            onChange={(e) =>
                              setChatbotForm((prev) => ({
                                ...prev,
                                rateLimitPerMinute: e.target.value,
                              }))
                            }
                            className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Allowed Origins */}
                    <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                      <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white mb-1.5">
                        Allowed Origins
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Add the full domain of every website that is allowed to embed
                        this chatbot widget (no trailing slash).
                      </p>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          placeholder="https://www.example.com"
                          value={originInput}
                          onChange={(e) => setOriginInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addOrigin();
                            }
                          }}
                          className="flex-1 px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                        />
                        <button
                          type="button"
                          onClick={addOrigin}
                          className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 dark:text-white rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                        >
                          <FaPlus size={10} /> Add
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {chatbotForm.allowedOrigins.map((origin) => (
                          <div
                            key={origin}
                            className="flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 rounded text-xs text-gray-700 dark:text-gray-300"
                          >
                            <span>{origin}</span>
                            <button
                              type="button"
                              onClick={() => removeOrigin(origin)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <FaTimes size={10} />
                            </button>
                          </div>
                        ))}
                        {chatbotForm.allowedOrigins.length === 0 && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            No origins added yet
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ---------------- TAB: KNOWLEDGE BASE ---------------- */}
                {chatbotSubTab === "knowledge" && chatbotExists && (
                  <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700 space-y-5">
                    <div>
                      <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                        <FaDatabase size={12} /> Knowledge Base
                        {knowledgeSources.length > 0 && (
                          <span className="text-[10px] font-normal px-2 py-0.5 bg-[#F3E8FF] dark:bg-purple-900/40 text-[#5E35A6] dark:text-[#C4B5FD] rounded-full">
                            {knowledgeSources.length}
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Give your bot extra context by pasting text or uploading files
                        (PDF, DOCX, TXT, MD, CSV). This content is used to answer
                        questions more accurately.
                      </p>
                    </div>

                    {/* Add pasted text */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-100 dark:border-gray-700">
                      <label className="flex items-center gap-1.5 text-xs font-medium mb-2 dark:text-gray-300">
                        <FaAlignLeft size={10} /> Add Pasted Text
                      </label>
                      <input
                        type="text"
                        placeholder="Title (optional, e.g. Refund Policy)"
                        value={knowledgeTitle}
                        onChange={(e) => setKnowledgeTitle(e.target.value)}
                        className="w-full px-2.5 py-1.5 mb-2 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                      />
                      <textarea
                        rows={3}
                        placeholder="Paste the text content you want the bot to know about..."
                        value={knowledgeContent}
                        onChange={(e) => setKnowledgeContent(e.target.value)}
                        className="w-full px-2.5 py-1.5 mb-2 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] resize-y"
                      />
                      <button
                        type="button"
                        onClick={addTextKnowledge}
                        disabled={isAddingKnowledgeText || !knowledgeContent.trim()}
                        className="px-3 py-1.5 text-xs bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50 transition-colors flex items-center gap-1.5"
                      >
                        <FaPlus size={10} />
                        {isAddingKnowledgeText ? "Adding..." : "Add Text"}
                      </button>
                    </div>

                    {/* Upload file */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-100 dark:border-gray-700">
                      <label className="flex items-center gap-1.5 text-xs font-medium mb-1.5 dark:text-gray-300">
                        <FaFileUpload size={10} /> Upload File
                      </label>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                        Supported formats: PDF, DOCX, TXT, MD, CSV.
                      </p>
                      <input
                        type="text"
                        placeholder="Title (optional, defaults to file name)"
                        value={knowledgeFileTitle}
                        onChange={(e) => setKnowledgeFileTitle(e.target.value)}
                        className="w-full px-2.5 py-1.5 mb-2 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                      />
                      <div className="flex flex-col md:flex-row gap-2">
                        <input
                          type="file"
                          accept=".pdf,.docx,.txt,.md,.csv"
                          onChange={(e) => setKnowledgeFile(e.target.files?.[0] || null)}
                          className="flex-1 px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                        />
                        <button
                          type="button"
                          onClick={uploadKnowledgeFile}
                          disabled={isUploadingKnowledgeFile || !knowledgeFile}
                          className="px-3 py-1.5 text-xs bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50 transition-colors flex items-center gap-1.5 whitespace-nowrap"
                        >
                          <FaFileUpload size={10} />
                          {isUploadingKnowledgeFile ? "Uploading..." : "Upload File"}
                        </button>
                      </div>
                    </div>

                    {/* List of knowledge sources */}
                    <div>
                      <h4 className="cfg-heading text-xs font-medium mb-2 dark:text-gray-300">
                        Sources
                      </h4>
                      {isLoadingKnowledge ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Loading knowledge sources...
                        </p>
                      ) : knowledgeSources.length === 0 ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          No knowledge sources added yet.
                        </p>
                      ) : (
                        <div className="space-y-1.5">
                          {knowledgeSources.map((source) => (
                            <div
                              key={source.id}
                              className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded"
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                {knowledgeSourceIcon(source.sourceType)}
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                                    {source.title}
                                  </p>
                                  <p className="text-[10px] text-gray-400">
                                    {source.sourceType}
                                    {typeof source.characterCount === "number"
                                      ? ` · ${source.characterCount.toLocaleString()} chars`
                                      : ""}
                                    {source.addedAt
                                      ? ` · Added ${formatDate(source.addedAt)}`
                                      : ""}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => deleteKnowledgeSource(source.id)}
                                disabled={deletingSourceId === source.id}
                                className="text-gray-400 hover:text-red-500 disabled:opacity-50 flex-shrink-0 ml-3"
                              >
                                <FaTrash size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ---------------- TAB: WIDGET & EMBED ---------------- */}
                {chatbotSubTab === "deploy" && chatbotExists && (
                  <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                    <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                      <FaKey size={12} /> Widget &amp; Embed
                    </h3>

                    {chatbotConfig?.widgetKey ? (
                      <>
                        <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                          Widget Key
                        </label>
                        <div className="flex flex-col md:flex-row gap-2 mb-4">
                          <div className="flex-1 relative">
                            <input
                              type={showWidgetKey ? "text" : "password"}
                              readOnly
                              value={chatbotConfig.widgetKey}
                              className="w-full px-2.5 py-1.5 text-xs pr-8 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 bg-gray-50"
                            />
                            <button
                              type="button"
                              onClick={() => setShowWidgetKey(!showWidgetKey)}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700"
                            >
                              {showWidgetKey ? <FaEyeSlash size={11} /> : <FaEye size={11} />}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              copyToClipboard(chatbotConfig.widgetKey, "Widget key copied")
                            }
                            className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 dark:text-white rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5"
                          >
                            <FaCopy size={10} /> Copy
                          </button>
                          <button
                            type="button"
                            onClick={regenerateWidgetKey}
                            disabled={isRegeneratingKey}
                            className="px-3 py-1.5 text-xs bg-[#F3E8FF] dark:bg-purple-900/30 text-[#5E35A6] dark:text-[#C4B5FD] rounded hover:bg-[#F3E8FF] dark:hover:bg-purple-900/50 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                          >
                            <FaSync
                              size={10}
                              className={isRegeneratingKey ? "animate-spin" : ""}
                            />
                            {isRegeneratingKey ? "Regenerating..." : "Regenerate"}
                          </button>
                        </div>

                        <label className="flex items-center gap-1.5 text-xs font-medium mb-1 dark:text-gray-300">
                          <FaCode size={10} /> Embed Snippet
                        </label>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                          Paste this before the closing &lt;/body&gt; tag on the external
                          website (after adding its domain to Allowed Origins in the
                          Behavior &amp; Limits tab).
                        </p>
                        <div className="relative">
                          <pre className="p-3 bg-gray-900 text-gray-100 rounded overflow-x-auto text-[11px] leading-relaxed">
{embedSnippet}
                          </pre>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(embedSnippet, "Embed snippet copied")}
                            className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-gray-100 rounded text-[10px] hover:bg-gray-600 transition-colors flex items-center gap-1"
                          >
                            <FaCopy size={9} /> Copy
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Save your chatbot configuration to generate a widget key and
                        embed snippet.
                      </p>
                    )}
                  </div>
                )}

                {/* ---------------- TAB: TEST BOT ---------------- */}
                {chatbotSubTab === "test" && chatbotExists && (
                  <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white">
                        Test Your Bot
                      </h3>
                      {testMessages.length > 0 && (
                        <button
                          onClick={resetTestConversation}
                          className="text-xs text-gray-500 dark:text-gray-400 hover:text-[#673AB7] dark:hover:text-[#8B5CF6]"
                        >
                          Reset conversation
                        </button>
                      )}
                    </div>

                    <div className="border border-gray-100 dark:border-gray-700 rounded-lg p-3 h-56 overflow-y-auto bg-gray-50 dark:bg-gray-900/50 mb-3 flex flex-col gap-2.5">
                      {testMessages.length === 0 && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 m-auto">
                          Send a message to preview how your bot responds.
                        </p>
                      )}
                      {testMessages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`max-w-[80%] px-2.5 py-1.5 rounded-lg text-xs ${
                            msg.role === "user"
                              ? "self-end bg-[#673AB7] text-white"
                              : "self-start bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-600"
                          }`}
                        >
                          {msg.text}
                        </div>
                      ))}
                      {isSendingTest && (
                        <div className="self-start px-2.5 py-1.5 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-400 border border-gray-100 dark:border-gray-600">
                          Typing...
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 items-end">
                      <textarea
                        ref={autoResizeRef}
                        rows={1}
                        placeholder="Ask something..."
                        value={testInput}
                        onChange={(e) => setTestInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (!isSendingTest) {
                              sendTestMessage();
                            }
                          }
                        }}
                        className="flex-1 px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] resize-none overflow-y-auto leading-relaxed transition-[height] duration-150 ease-in-out"
                      />
                      <button
                        onClick={sendTestMessage}
                        disabled={isSendingTest || !testInput.trim()}
                        className="px-3 py-1.5 text-xs bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50 transition-colors flex items-center justify-center"
                      >
                        <FaPaperPlane size={10} />
                      </button>
                    </div>
                  </div>
                )}

                {/* ---------------- TAB: FAQs (nested categories via parentId) ---------------- */}
                {chatbotSubTab === "faqs" && chatbotExists && (
                  <div className="space-y-5">
                    {/* Add / Edit FAQ form */}
                    <div className="relative p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                      <label className="absolute top-4 right-4 flex items-center gap-1.5 cursor-pointer select-none">
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {faqForm.active ? "Active" : "Hidden"}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setFaqForm((prev) => ({ ...prev, active: !prev.active }))
                          }
                          className={`relative w-8 h-4 rounded-full transition-colors ${
                            faqForm.active
                              ? "bg-[#673AB7]"
                              : "bg-gray-300 dark:bg-gray-600"
                          }`}
                        >
                          <span
                            className={`absolute top-[2px] left-[2px] w-3 h-3 bg-white rounded-full transition-transform ${
                              faqForm.active ? "translate-x-4" : ""
                            }`}
                          />
                        </button>
                      </label>

                      <div className="mb-3 pr-24">
                        <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-1.5">
                          <FaQuestionCircle size={12} />
                          {editingFaqId ? "Edit FAQ" : "Add a FAQ"}
                          {faqs.length > 0 && (
                            <span className="text-[10px] font-normal px-2 py-0.5 bg-[#F3E8FF] dark:bg-purple-900/40 text-[#5E35A6] dark:text-[#C4B5FD] rounded-full">
                              {faqs.length}
                            </span>
                          )}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          FAQs show up as quick-reply buttons in the widget menu. Leave
                          "Parent Category" empty for a top-level button, or nest a
                          question under an existing one to build a multi-level menu.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                            Question
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. What are your business hours?"
                            value={faqForm.question}
                            onChange={(e) =>
                              setFaqForm((prev) => ({ ...prev, question: e.target.value }))
                            }
                            className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                            Answer
                          </label>
                          <textarea
                            rows={2}
                            placeholder="We're open Monday–Friday..."
                            value={faqForm.answer}
                            onChange={(e) =>
                              setFaqForm((prev) => ({ ...prev, answer: e.target.value }))
                            }
                            className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] resize-y"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                            Parent Category{" "}
                            <span className="text-[10px] font-normal text-gray-400">
                              (optional — nests this under an existing question)
                            </span>
                          </label>
                          <select
                            value={faqForm.parentId || ""}
                            onChange={(e) =>
                              setFaqForm((prev) => ({
                                ...prev,
                                parentId: e.target.value || null,
                              }))
                            }
                            className="w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                          >
                            <option value="">No parent (top-level menu item)</option>
                            {parentFaqOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {"— ".repeat(getFaqDepth(option.id, faqs))}
                                {option.question}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                          <button
                            type="button"
                            onClick={saveFaq}
                            disabled={isSavingFaq}
                            className="px-4 py-1.5 text-xs bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50 transition-colors h-[32px]"
                          >
                            {isSavingFaq
                              ? "Saving..."
                              : editingFaqId
                              ? "Update FAQ"
                              : "Add FAQ"}
                          </button>
                          {editingFaqId && (
                            <button
                              type="button"
                              onClick={resetFaqForm}
                              className="px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors h-[32px]"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Tree list of FAQs */}
                    <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                      <h4 className="cfg-heading text-xs font-medium mb-2.5 dark:text-gray-300">
                        All FAQs
                      </h4>
                      {isLoadingFaqs ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Loading FAQs...
                        </p>
                      ) : faqs.length === 0 ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          No FAQs added yet. Use the form above to add your first one.
                        </p>
                      ) : (
                        <div className="space-y-1.5">
                          {faqTree.map((node) => renderFaqNode(node))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ---------------- TAB: CONVERSATIONS ---------------- */}
                {chatbotSubTab === "conversations" && chatbotExists && (
                  <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
                    <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-1.5 mb-3">
                      <FaHistory size={12} /> Conversations
                    </h3>

                    {selectedConversation ? (
                      <div>
                        <button
                          onClick={() => setSelectedConversation(null)}
                          className="flex items-center gap-1 text-xs text-[#673AB7] dark:text-[#8B5CF6] mb-2 hover:underline"
                        >
                          <FaChevronLeft size={9} /> Back to all conversations
                        </button>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                          Session: {selectedConversation.sessionId} · Last activity:{" "}
                          {formatDate(selectedConversation.lastActivityAt)}
                        </p>
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {(selectedConversation.messages || []).map((msg, idx) => (
                            <div
                              key={idx}
                              className={`max-w-[85%] px-2.5 py-1.5 rounded-lg text-xs ${
                                msg.role === "user"
                                  ? "ml-auto bg-[#F3E8FF] dark:bg-purple-900/40 text-[#4C2D85] dark:text-[#E9D5FF]"
                                  : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                              }`}
                            >
                              <p>{msg.text}</p>
                              <p className="text-[9px] text-gray-400 mt-1">
                                {formatDate(msg.timestamp)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : isLoadingConversations ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Loading conversations...
                      </p>
                    ) : conversations.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        No conversations yet.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {conversations.map((conv) => (
                          <button
                            key={conv.sessionId}
                            onClick={() => fetchConversationDetail(conv.sessionId)}
                            disabled={isLoadingConversationDetail}
                            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                          >
                            <div>
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                                {conv.sessionId}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                {conv.messages?.length || 0} messages · Last active{" "}
                                {formatDate(conv.lastActivityAt)}
                              </p>
                            </div>
                            <FaChevronRight size={10} className="text-gray-400" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Save / Delete actions — always visible so changes on any tab can be saved */}
                <div className="flex flex-col md:flex-row gap-2.5 md:items-center md:justify-between pt-2">
                  <button
                    onClick={saveChatbotConfig}
                    disabled={isSavingChatbot}
                    className="px-4 py-1.5 text-xs bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50 transition-colors h-[32px]"
                  >
                    {isSavingChatbot
                      ? "Saving..."
                      : chatbotExists
                      ? "Save Changes"
                      : "Create Chatbot"}
                  </button>

                  {chatbotExists && (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="flex items-center gap-1.5 px-4 py-1.5 text-xs border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors h-[32px]"
                    >
                      <FaTrash size={10} /> Delete Configuration
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Delete chatbot configuration confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-5">
            <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white mb-2">
              Delete chatbot configuration?
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
              This will permanently delete the chatbot configuration and all stored
              conversations for this company. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteChatbotConfig}
                disabled={isDeletingChatbot}
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isDeletingChatbot ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete FAQ confirmation modal */}
      {faqToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-5">
            <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white mb-2">
              Delete "{faqToDelete.question}"?
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
              {getDescendantIds(faqToDelete.id, faqs).length > 0
                ? `This is a category with ${
                    getDescendantIds(faqToDelete.id, faqs).length
                  } sub-question(s) nested under it. Deleting it will permanently delete this item AND all of its sub-questions. This action cannot be undone.`
                : "This will permanently delete this FAQ. This action cannot be undone."}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={cancelDeleteFaq}
                className="px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteFaq(faqToDelete.id)}
                disabled={deletingFaqId === faqToDelete.id}
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deletingFaqId === faqToDelete.id ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transcriptionconfig;